/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2016-2021, Bulb Inc.  For more information, please email contact@hellobulb.com.
 */

// Can we make a directive out of this which depends on the contenteditor directive, such that this directive,
// during the prelink, could create an instance of the extension, and give it to the contentEditor controller for
// use during the initialization of the editor, but since it is in a directive, we have access to such things as
// scope and other controllers, so we can do things like make frame changes and such?

/**
 * Extension to allow the insertion of bulb media blocks into the medium editor clone.
 */
angular.module('bulbApp.contentEditorModule')
  .factory(
    'BulbMediumInsertBlockExtensionFactory',
    [
      '$', '$compile', '$cookies', '$filter', '$interval',
      '$q', '$timeout', '$translate', '$window', 'MediumEditor', 'Restangular',
      'BulbAuthService', 'BulbAssetLibraryGoogleDriveService',
      'BulbAssetLibraryGooglePhotoService', 'BulbAssetLibraryOneDriveService',
      'BulbAssetLibrarySnapshotPdfService', 'BulbAssetLibraryService',
      'BulbAssetLibraryUnsplashService', 'BulbAssetLibraryUploadService',
      'BulbBrowserService', 'BulbBusyCoverService', 'BulbBusyService',
      'BulbCommentService', 'BulbErrorService', 'BulbModalService', 'RecordEventService',
      'SerialSaveService', 'UnitStateStore', 'CONFIG', 'CONTENT_EDITOR',
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      BulbMediumInsertBlockExtensionFactory,
    ],
  );

function BulbMediumInsertBlockExtensionFactory(
  $,
  $compile,
  $cookies,
  $filter,
  $interval,
  $q,
  $timeout,
  $translate,
  $window,
  MediumEditor,
  Restangular,
  BulbAuthService,
  BulbAssetLibraryGoogleDriveService,
  BulbAssetLibraryGooglePhotoService,
  BulbAssetLibraryOneDriveService,
  BulbAssetLibrarySnapshotPdfService,
  BulbAssetLibraryService,
  BulbAssetLibraryUnsplashService,
  BulbAssetLibraryUploadService,
  BulbBrowserService,
  BulbBusyCoverService,
  BulbBusyService,
  BulbCommentService,
  BulbErrorService,
  BulbModalService,
  RecordEventService,
  SerialSaveService,
  UnitStateStore,
  CONFIG,
  CONTENT_EDITOR,
) {
  // Key Codes that can change or modify the selection:
  const selectionKeyCodes = [
    8, // backspace
    13, // enter
    37, // left arrow
    38, // up arrow
    39, // right arrow
    40, // down arrow
    46, // delete
  ]; //

  const createNewParagraphData = function createNewParagraphData() {
    return BulbCommentService.getParagraphIds(1).then((paragraphIds) => `<p id="${paragraphIds[0]}"><br/></p>`);
  };

  const BulbInsertBlockExtension = MediumEditor.Extension.extend({
    name: 'bulbInsertBlock',

    selection: null,

    // TODO UNDO : how do we undo/redo an upload?

    mediaFileUploadOptions: {
      url: '/b/rest/assets',
      paramName: 'files[]',
      // TODO CLEAN : get these file types from config
      acceptFileTypes: /(\.|\/)(gif|jpe?g|png|3g2|3gp|asf|avi|flv|m4v|mkv|mov|mp4|mpg|vob|wmv|quicktime)$/i,
      maxChunkSize: 5242880, // TODO CLEAN : need to get this from CONFIG after merge with dev
      maxFileSize: 10000000000, // TODO CLEAN : need to get this from CONFIG after merge with dev
      headers: {
        'X-XSRF-TOKEN': $cookies.get('XSRF-TOKEN'),
      },
    },

    attachmentUploadOptions: {
      url: '/b/rest/assets',
      paramName: 'files[]',
      maxChunkSize: 5242880,
      maxFileSize: 10000000000, // TODO CLEAN : need to get this from CONFIG after merge with dev
      headers: {
        'X-XSRF-TOKEN': $cookies.get('XSRF-TOKEN'),
      },
    },

    defaults: {
      messages: { // TODO CLEAN : these need to be translated, or removed based on better error handling.
        acceptFileTypesError: 'This file is not in a supported format: ',
        maxFileSizeError: 'This file is too big: ',
      },
    },

    init:  function init() {
      // NOTE: This assumes that this extension is tied to exactly one editor.  If more than one editor is
      // in use, then separate instances of the extension should be created for each editor instance.
      this.editor = this.getEditorElements()[0];

      // Adding this factory onto the window object, so we could potentially reference it in our Angular Framework.
      // We could upgrade this factory to a pure Angular Service, which would be better, however the time to refactor
      // This service would outweigh any cost ( at the moment -- we are going to have to do this at some point ).
      $window.bulbVars.BulbMediumInsertBlockExtensionFactory = this;

      this.options = angular.merge({}, this.defaults, this.options);

      this.subscribe('editableClick', this.handleClick.bind(this));
      this.subscribe('editableInput', this.handleInput.bind(this));
      this.subscribe('editableKeydown', this.handleKeydown.bind(this));
      this.subscribe('editableKeyup', this.handleKeyup.bind(this));
      this.subscribe('blur', this.handleBlur.bind(this));
      this.subscribe('focus', this.updateButtons.bind(this));

      this.scope.$on('command-cancel-upload', this.cancelUpload.bind(this));
      this.scope.$on('command-delete', this.deleteSelectedBlock.bind(this));

      this.page = this.scope.contentEditorCtrl.page;

      // this.getEditorOption('elementsContainer').appendChild(this.getButtonsElement());
      // this.editor.appendChild(this.getButtonsElement());

      // Poll for selection changes for mobile devices.
      if (BulbBrowserService.isTouchDevice() && BulbBrowserService.isMobile()) { // jshint ignore:line
        let previousSelection = this.base.exportSelection();
        const that = this; // jshint ignore:line
        $interval(() => {
          const currentSelection = that.base.exportSelection();
          if (!angular.equals(previousSelection, currentSelection)) {
            previousSelection = currentSelection;
            that.base.checkSelection();
            that.updateButtons();
          }
        }, 200);
      }
    },

    handleClick: function handleClick(event) {
      // Check to see if an inserted media element has been selected.
      const $element = $(event.target);
      const $selectableBlockElement = $element.closest('[selectable-block]');
      if ($selectableBlockElement.length) {
        // We are inside a block.  However, check to see if we are also inside a contenteditable, because
        // that will have precidence over the block.
        const $editableElement = $element.closest('[contenteditable="true"]');
        if ($editableElement.length && jQuery.contains($selectableBlockElement[0], $editableElement[0])) {
          this.unselectBlock();
        } else {
          this.selectBlock($selectableBlockElement[0]);
        }
      } else {
        this.unselectBlock();
      }
    },
    handleEnterPress: function handleEnterPress() {
      const mediaEditor = $('.bulb-insert-block-buttons-media').parent().parent();
      $(mediaEditor.find('p')[0]).addClass('bulb-media-active');
      $(mediaEditor.find('div')[0]).removeClass('hide');
      mediaEditor.attr('data-medium-focused', 'true');
    },
    unselectBlock: function unselectBlock() {
      $(this.editor).find('.selected-block').removeClass('selected-block');
      this.blockSelected(null);
      this.selection = null;
      this.updateButtons();
    },

    handleKeydown: function handleKeydown(event) {
      // CTRL+HOME and CTRL+END behave the same, regardless of the current selection:
      if (event.ctrlKey) {
        if (event.keyCode === 36) { // CTRL+HOME
          const $firstChild = $(this.editor).children().first();
          if ($firstChild.is('content-block')) {
            this.selectBlock($firstChild[0]);
          } else {
            this.placeSelectionCaretAtStart($firstChild[0]);
          }
          event.preventDefault();
          this.ensureElementIsVisible($firstChild[0]);
          return;
        } if (event.keyCode === 35) { // CTRL+END
          const $lastChild = $(this.editor).children('.bulb-content-trailer').prev();
          if ($lastChild.is('content-block')) {
            this.selectBlock($lastChild[0]);
            $(this.editor).trigger('keypress');
          } else {
            this.placeSelectionCaretAtEnd($lastChild[0]);
          }
          event.preventDefault();
          this.ensureElementIsVisible($lastChild[0]);
          return;
        }
      }

      if (!selectionKeyCodes.includes(event.keyCode)) {
        // non-navigation key codes are all handled normally
        return;
      }

      delete this.$preNavElement;

      if (this.selection) {
        // Handle keys when a block is selected
        this.selectedBlockKeydown(event);
        return;
      }

      const selection = this.document.getSelection();
      const $focusNode = $(selection.focusNode);
      if ($focusNode.closest('.bulb-edit-caption').length) {
        // The selection is inside a caption, so handle it specially:
        this.captionKeydown(event);
        return;
      }

      // Selection is not a block or a caption, so it must be normal text:
      this.textKeydown(event);
    },

    handleKeyup: function handleKeyup(event) {
      // It is very difficult to determine if the caret is on the first or last line of a paragraph in order to
      // handle an up arrow and down arrow correctly.  Instead, go ahead and let the normal navigation occur, and
      // if we see that the element changes, then we know that changing the selection is the correct thing to do.
      // At that point, handle the selection as appropriate, based on the previous caret location.
      if (this.$preNavElement) {
        const { $preNavElement } = this;

        const selection = this.document.getSelection();
        const $focusNode = $(selection.focusNode);
        let $currentElement;
        let $nextElement;

        if ($preNavElement.is('.bulb-edit-caption')) {
          $currentElement = $focusNode.closest('.bulb-page-content > *');
          if ($preNavElement.closest($currentElement).length === 0) {
            // Was previously in a caption, but now is in another element, so we know we need to navigate:
            if (event.keyCode === 38) { // up arrow
              if (!event.shiftKey) {
                // Not doing a multi-select, so we can simply select the content-block which this
                // caption is a part of
                this.selectBlock($preNavElement.closest('content-block')[0]);
              } else {
                // We don't allow a multi-select to go beyond the caption, so restore the selection to
                // what it was before the keyboard navigation.
                this.base.restoreSelection();
              }
            } else { // down arrow
              $nextElement = $preNavElement.next();
              if ($nextElement.is('content-block')) {
                // We don't allow multi-select between blocks, only select the next block if we
                // aren't ina multi-select.
                if (!event.shiftKey) {
                  this.selectBlock($nextElement[0]);
                } else {
                  this.base.restoreSelection();
                }
              } else if (event.shiftKey) {
                // The next element isn't a content block, so we just navigate, unless it is a
                // multi-select, as we don't allow the selection to span the caption and subsequent
                // text
                this.base.restoreSelection();
              }
            }
          }
          event.preventDefault();
        } else {
          $currentElement = $focusNode.closest('.bulb-page-content > p');

          if (!$preNavElement.is($currentElement)) {
            if (event.keyCode === 38) { // up arrow
              const $prevElement = $preNavElement.prev();
              if ($prevElement.is('content-block')) {
                // Because the previous element is a block, if we are in a multi-select, restore
                // the previous selection to prevent it from including a block.  Otherwise, if the
                // block doesn't have a caption, select the block.  We don't have to do anything if
                // the block does have a caption, as the default navigation will have already given it
                // focus.
                if (event.shiftKey) {
                  this.base.restoreSelection();
                } else {
                  const $captionElement = $prevElement.find('.bulb-edit-caption');
                  if ($captionElement.length === 0) {
                    this.selectBlock($prevElement[0]);
                  }
                }
              }
            } else { // down arrow
              $nextElement = $preNavElement.next();
              if ($nextElement.is('content-block')) {
                // We always restore the previous selection here, because if a multi-select is taking
                // place, we don't want to allow it to try to include the block.  Alternatively, we
                // want to restore the selection if we select the next block, otherwise the caption
                // for the next block is left with focus.
                this.base.restoreSelection();

                if (!event.shiftKey) {
                  // Only select the block if not part of a multi-select.
                  this.selectBlock($nextElement[0]);
                }
              }
            }
            event.preventDefault();
          }
        }
      }

      if (selectionKeyCodes.includes(event.keyCode)) {
        this.updateButtons();
      }
    },

    placeSelectionCaretAtStart: function placeSelectionCaretAtStart(element) {
      this.unselectBlock();
      MediumEditor.selection.moveCursor(this.document, element, 0);
      this.ensureElementIsVisible(element);
    },

    placeSelectionCaretAtEnd: function placeSelectionCaretAtEnd(element) {
      this.unselectBlock();
      const range = this.document.createRange();
      range.selectNodeContents(element);
      range.collapse(false); // false collapses the selection at the end, rather than the start
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
      this.ensureElementIsVisible(element);
    },

    ensureElementIsVisible: function ensureElementIsVisible(element) {
      const rect = element.getBoundingClientRect();
      const isInViewport = (
        rect.top >= 0
        && rect.left >= 0
        && rect.bottom <= (window.innerHeight || this.document.documentElement.clientHeight)
        && rect.right <= (window.innerWidth || this.document.documentElement.clientWidth)
      );

      if (!isInViewport) {
        element.scrollIntoView();
      }
    },

    textKeydown: function textKeydown(event) {
      const selection = this.document.getSelection();
      const $focusNode = $(selection.focusNode);
      const $ancestorElement = $focusNode.closest('.bulb-page-content > *');
      let offset = { left: null, right: null };

      if ($ancestorElement.length !== 0) {
        offset = MediumEditor.selection.getCaretOffsets($ancestorElement[0]);
      }

      const $prevElement = $ancestorElement.prev();
      const $nextElement = $ancestorElement.next();

      switch (event.keyCode) {
        case 37: // Left Arrow
          if (offset.left === 0 && $prevElement.is('content-block')) {
            const $captionElement = $prevElement.find('.bulb-edit-caption');
            if ($captionElement.length) {
              this.placeSelectionCaretAtEnd($captionElement[0]);
            } else {
              this.selectBlock($prevElement[0]);
            }
            event.preventDefault();
          } else if (offset.left === 0 && !$prevElement[0]) {
            // page top
            this.focusPageTitle();
            event.preventDefault();
          }
          break;

        case 39: // Right Arrow
          if (offset.right === 0 && $nextElement.is('content-block')) {
            this.selectBlock($nextElement[0]);
            event.preventDefault();
          }
          break;

        case 38: // Up Arrow
          if (!$prevElement[0]) {
            // page top
            this.focusPageTitle(offset.left);
            event.preventDefault();
          }
          break;

        case 40: // Down Arrow
          this.base.saveSelection();
          this.$preNavElement = $ancestorElement;
          break;

        case 8: // Backspace
          // This is the beginning of the page.
          if (offset.left === 0 && ($prevElement.is('content-block') || !$prevElement[0])) {
            // If at the start of a paragraph following a block or with no previous element,
            // backspace does nothing.
            event.preventDefault();
          }
          break;

        case 46: // Delete
          if (offset.right === 0
            && offset.left !== 0
            && selection.isCollapsed
            && $nextElement.is('content-block')) {
            // at the end of a non-empty paragraph, before a block, and there isn't a selection, delete
            // does nothing.
            event.preventDefault();
          }
          break;
        // TODO DESIGN : how to handle pgup and pgdown?
      }

      this.updateButtons();
    },

    selectedBlockKeydown: function selectedBlockKeydown(event) {
      event.preventDefault();

      if (event.shiftKey) {
        // We don't allow multi-select with blocks in it, so we can just ignore any navigation from a block
        // if the shiftKey is down
        return;
      }

      const $selectedBlock = this.contentEditorCtrl.selectedBlockElement; // TODO CLEAN fix naming convention
      const $prevElement = $selectedBlock.prev();
      const $nextElement = $selectedBlock.next();
      let $captionElement;

      switch (event.keyCode) {
        case 37: // Left Arrow
        case 38: // Up Arrow
          if ($prevElement.is('content-block')) {
            $captionElement = $prevElement.find('.bulb-edit-caption');
            if ($captionElement.length) {
              this.placeSelectionCaretAtEnd($captionElement[0]);
            } else {
              this.selectBlock($prevElement[0]);
            }
          } else if ($prevElement) {
            this.placeSelectionCaretAtEnd($prevElement[0]);
          } else {
            // page top
            this.focusPageTitle();
            event.preventDefault();
          }
          break;

        case 39: // Right Arrow
        case 40: // Down Arrow
          $captionElement = $selectedBlock.find('.bulb-edit-caption');
          if ($captionElement.length !== 0) {
            this.placeSelectionCaretAtStart($captionElement[0]);
          } else if ($nextElement.is('content-block')) {
            this.selectBlock($nextElement[0]);
          } else if ($nextElement.is('p')) {
            this.placeSelectionCaretAtStart($nextElement[0]);
          } else {
            event.preventDefault();
          }
          break;

        case 13: // Enter
          let $newElement;
          if (event.ctrlKey) {
            $selectedBlock.before('<p><br/></p>');
            $newElement = $selectedBlock.prev();
          } else {
            $selectedBlock.after('<p><br/></p>');
            $newElement = $selectedBlock.next();
          }
          this.unselectBlock();
          MediumEditor.selection.select(this.document, $newElement[0], 0);
          break;

        case 8: // Backspace
        case 46: // Delete
          this.deleteSelectedBlock();
          break;

        // TODO DESIGN : how to handle pgup and pgdown?
      }

      this.updateButtons();
    },

    captionKeydown: function captionKeydown(event) {
      // TODO PERFORM : figure out how to gather these values only if a nav key is hit, rather than for every key.
      const selection = this.document.getSelection();
      const $focusNode = $(selection.focusNode);
      const $captionElement = $focusNode.closest('.bulb-edit-caption');
      const $block = $focusNode.closest('content-block');
      const offset = MediumEditor.selection.getCaretOffsets($captionElement[0]);

      switch (event.keyCode) {
        case 37: // Left Arrow
          if (offset.left === 0) {
            event.preventDefault();
            this.selectBlock($block[0]);
          }
          // otherwise, handle as normal
          break;

        case 39: // Right Arrow
          if (offset.right === 0) {
            const $nextElement = $block.next();
            if ($nextElement.is('content-block')) {
              this.selectBlock($nextElement[0]);
              event.preventDefault();
            }
          }
          // otherwise, just handle as normal
          break;

        case 38: // Up Arrow
        case 40: // Down Arrow
          this.base.saveSelection();
          this.$preNavElement = $captionElement;
          break;

        case 13: // Enter
          this.base.saveSelection();
          this.$preNavElement = $captionElement;

          const that = this;
          this.appendNewLine($(selection.focusNode).closest('content-block')).then((node) => {
            const $nextNode = node.next();
            $timeout(() => {
              that.placeSelectionCaretAtStart($nextNode[0]);
            });
          });

          event.preventDefault();
          break;

        case 8: // Backspace
          // If at beginning of caption, do nothing, else handle normally
          if (offset.left === 0) {
            event.preventDefault();
          }
          break;

        case 46: // Delete
          // if at end of caption, do nothing, else handle normally
          if (offset.right === 0) {
            event.preventDefault();
          }
          break;

        // TODO DESIGN : how to handle pgup and pgdown?
      }

      this.updateButtons();
    },

    selectBlock: function selectBlock(element) {
      const $element = $(element);
      // First, go up the heirarchy, looking for a 'selectable-block' element.
      let $selectableElement = $element.closest('[selectable-block]');
      if ($selectableElement.length === 0) {
        // Didn't find one going up, so try going down:
        $selectableElement = $element.find('[selectable-block]');
      }
      if ($selectableElement.length === 0) {
        // Didn't find a selectable element in either direction.
        // TODO CLEAN - do nothing, or select null?
        return;
      }

      $(this.editor).find('.selected-block').removeClass('selected-block');
      $selectableElement.addClass('selected-block'); // TODO CLEAN : improve name of this

      this.selection = $selectableElement[0];

      // MediumEditor.selection.selectNode uses selectNodeContents, so we really want to select the parent
      // of the content, which means we want to select the ng-transclude node:
      const transcludeNode = $selectableElement.children('[ng-transclude]')[0];

      // This is almost the same as MediumEditor's selectElement() api, but this is smarter
      // as it doesn't do a focus if the element is already has focus.  Doing such a focus sometimes causes
      // the selection to be lost.
      MediumEditor.selection.selectNode(transcludeNode, this.document);
      const selectedEditor = MediumEditor.selection.getSelectionElement(this.window);
      const focusedElement = this.base.getFocusedElement();
      if (selectedEditor !== focusedElement) {
        this.base.events.focusElement(selectedEditor);
      }

      this.ensureElementIsVisible($selectableElement[0]);
      this.blockSelected($selectableElement.closest('content-block')[0]);
      this.updateButtons();
    },

    handleBlur: function handleBlur(event, element) {
      jQuery(element).find('.bulb-media-active').removeClass('bulb-media-active');
      this.hideButtons(element);
      $(this.editor).find('.selected-block').removeClass('selected-block');
      this.blockSelected(null);
      this.selection = null;
    },

    handleInput: function handleInput(/* event, element */) {
      this.updateButtons();

      // remove duplicate id
      const selection = this.document.getSelection();
      const $focusNode = $(selection.focusNode);
      const $pElement = $focusNode.closest('.bulb-page-content > *');

      if ($pElement) {
        // BULBAPP-631: target property not added to last link added on page.
        // So, whenever new link is created, we check if target property is not added, then we add it.
        const anchorTag = ($pElement.length > 0) ? $pElement[0].childNodes[0] : null;
        if (anchorTag && anchorTag.tagName === 'A' && !anchorTag.hasAttribute('target')) {
          anchorTag.setAttribute('target', '_blank');
        }

        const id = $pElement.attr('id');
        const count = $(`[id=${id}]`).length;
        if (count !== 1) {
          // duplicate id
          BulbCommentService.getParagraphIds(1).then((paragraphIds) => {
            $pElement.attr('id', paragraphIds[0]);
          });
        }
      }
    },

    updateButtons: function updateButtons() {
      if (!this.buttons) {
        this.editor.appendChild(this.getButtonsElement());
      }

      $(this.editor).find('.bulb-media-active').removeClass('bulb-media-active');

      const selection = this.window.getSelection();
      // if there isn't a selection, then don't show any buttons
      if (!selection || selection.rangeCount === 0) {
        this.hideButtons();
        return;
      }

      const range = selection.getRangeAt(0);
      let current = range.commonAncestorContainer;
      current = this.getElementAncestor(current);

      // the selection is outside the editor is selected, so hide the buttons
      if (current === null) {
        this.hideButtons();
        return;
      }

      // Only show the buttons if the current selection is an editable element AND the immediate child of the
      // editor AND empty.
      const $current = $(current);
      if ($current.is('p') && current.isContentEditable && current.parentElement === this.editor
        && $current.text().trim() === '') {
        this.showButtons(current);
        $current.addClass('bulb-media-active');
      } else {
        this.hideButtons();
      }
    },

    showButtons: function showButtons(element) {
      this.positionButtons(element);
      this.buttons.classList.remove('hide');

      const $options = $(this.buttons).find('.bulb-insert-block-buttons-options');
      $options.addClass('hide');
    },

    hideButtons: function hideButtons() {
      this.buttons.classList.add('hide');
      $(this.buttons).find('.bulb-insert-block-buttons-show').removeClass('bulb-insert-block-button-rotate');
      $(this.editor).find('.bulb-media-active').removeClass('bulb-media-active');
    },

    getButtonsElement: function getButtonsElement() {
      if (!this.buttons) {
        this.buttons = this.createButtons();
      }

      return this.buttons;
    },

    createButtons: function createButtons() {
      // TODO POLISH : add tooltips

      const buttons = this.document.createElement('div');
      buttons.id = `bulb-insert-block-buttons-${this.getEditorId()}`;
      buttons.setAttribute('class', 'bulb-insert-block-buttons bulb-media-transient hide');
      buttons.setAttribute('contentEditable', false);

      const videoButton = this.document.createElement('button');
      videoButton.setAttribute('class', 'bulb-insert-block-buttons-media aero-button bulb-aero-blue-button');
      videoButton.setAttribute('tabindex', '0');
      videoButton.innerHTML = `<span class="button-icon fas fa-video"></span>${
        $translate.instant('content_block_type_VIDEO')}`;
      buttons.appendChild(videoButton);
      this.on(videoButton, 'click', this.handleVideoButtonClick.bind(this));

      const imageButton = this.document.createElement('button');
      imageButton.setAttribute('class', 'bulb-insert-block-buttons-media aero-button bulb-aero-blue-button');
      imageButton.setAttribute('tabindex', '0');
      imageButton.innerHTML = `<span class="button-icon far fa-image"></span>${
        $translate.instant('content_block_type_IMAGE')}`;
      buttons.appendChild(imageButton);
      this.on(imageButton, 'click', this.handleMediaButtonClick.bind(this));

      if (BulbAuthService.currentUser.features.AddFileFeature.enabled) {
        const attachmentButton = this.document.createElement('button');
        attachmentButton.setAttribute('class', 'bulb-insert-block-buttons-media aero-button bulb-aero-blue-button');
        attachmentButton.setAttribute('tabindex', '0');
        attachmentButton.innerHTML = `<span class="button-icon fa fa-paperclip"></span>${
          $translate.instant('content_block_type_FILE')}`;
        buttons.appendChild(attachmentButton);
        this.on(attachmentButton, 'click', this.handleAttachmentButtonClick.bind(this));
      }

      if (BulbAuthService.currentUser.features.AudioFeature.enabled) {
        const audioButton = this.document.createElement('button');
        audioButton.setAttribute('class', 'bulb-insert-block-buttons-media aero-button bulb-aero-blue-button');
        audioButton.setAttribute('tabindex', '0');
        audioButton.innerHTML = `<span class="button-icon fas fa-music"></span>${
          $translate.instant('content_block_type_AUDIO')}`;
        buttons.appendChild(audioButton);
        this.on(audioButton, 'click', this.handleAudioButtonClick.bind(this));
      }
      if (BulbAuthService.currentUser.features.AddEmbedFeature.enabled) {
        const embedButton = this.document.createElement('button');
        embedButton.setAttribute('class', 'bulb-insert-block-buttons-media aero-button bulb-aero-blue-button');
        embedButton.setAttribute('tabindex', '0');
        embedButton.innerHTML = `<span class="button-icon fa fa-sign-in"></span>${
          $translate.instant('content_block_type_URL')}`;
        buttons.appendChild(embedButton);
        // Unfortunately click event is not firing on Enter key press for this button which
        // is happening for other buttons above.On adding the keydown event explicitly
        // it is surprisingly opening modal twice.So to fix it we are ignoring keydown event in
        // handleEmbedButtonClick method.
        this.on(embedButton, 'click', this.handleEmbedButtonClick.bind(this));
      }

      return buttons;
    },

    positionButtons: function positionButtons() {
      const selection = this.window.getSelection();
      let range; let
        current;
      if (!selection || selection.rangeCount === 0) {
        current = this.base.getFocusedElement();
      } else {
        range = selection.getRangeAt(0);
        current = range.commonAncestorContainer;
      }

      current = this.getElementAncestor(current);

      this.buttons.style.top = `${current.offsetTop}px`;
    },

    getElementAncestor: function getElementAncestor(element) {
      // if (element === this.editor) {
      //
      //   var editorElementChildren = jQuery('.bulb-page-content').children();
      //   for (var i = 0; i < editorElementChildren.length; i++) {
      //
      //     var currentChild = jQuery(editorElementChildren[i]);
      //     if (currentChild.prop('tagName').toLowerCase() !== 'p' &&
      //       currentChild.prop('tagName').toLowerCase() !== 'content-block') {
      //
      //       if (currentChild.prop('tagName').toLowerCase() === 'div' &&
      //         currentChild.attr('class').indexOf('bulb-insert-block-buttons') >= 0) {
      //         break;
      //       }
      //
      //       var siblingChild = currentChild.prev().get(0);
      //       currentChild.replaceWith();
      //       this.placeSelectionCaretAtEnd(siblingChild);
      //     }
      //   }
      // }

      {
        // for firefox
        // The following result is different between firefox and other browsers.
        // 'selection.getRangeAt(0).commonAncestorContainer'
        if (element === this.editor) {
          return $(element).children().eq(0)[0];
        }
      }

      if (!element || element.parentElement === this.editor) {
        return element;
      }
      return this.getElementAncestor(element.parentNode);
    },

    handleShowButtonClick: function handleShowButtonClick(event) {
      const $options = $(this.buttons).find('.bulb-insert-block-buttons-options');
      if ($options.hasClass('hide')) {
        $options.removeClass('hide');
        $(this.buttons).find('.bulb-insert-block-buttons-show').addClass('bulb-insert-block-button-rotate');
      } else {
        $options.addClass('hide');
        $(this.buttons).find('.bulb-insert-block-buttons-show').removeClass('bulb-insert-block-button-rotate');
      }
      event.stopPropagation();
    },

    initContentBlock: function initContentBlock($place, tempId) {
      this.hideButtons();

      $place.attr('id', tempId);
      $place.addClass('bulb-media-temp-placeholder');
      if (this.isLastContent($place)) {
        this.appendNewLine($place);
      }
    },

    $lastPlace: null,
    $tempId: undefined,
    addFileHandler: function addFileHandler(event, data) {
      // hide placeholder
      this.base.getExtensionByName('placeholder').hidePlaceholder(this.editor);
      data.tempId = this.$tempId;
      BulbAssetLibraryService.dialog.dismissAllModals();
      if (!data.tempId) {
        data.tempId = this.generateTemporaryId();
        this.initContentBlock(this.$lastPlace, data.tempId);
      }
      this.$tempId = undefined;
    },

    submitFileHandler: function submitFileHandler(event, data) {
      switch (data.entry.type) {
        case 'IMAGE':
          this.showImagePreview(data.target.result, data);
          break;
        default:
          this.showUploadingFileProgressbar(data);
          break;
      }
    },

    progressFileHandler: function progressFileHandler(event, data, progress) {
      const { tempId } = data.entry;
      switch (data.entry.type) {
        case 'IMAGE':
          // TODO CLEAN : should this be a
          // function call instead of embedded
          // here in the switch?
          const $progressbar = $(this.editor).find(`#${tempId} .bulb-media-image-progress`);

          $progressbar.css('width', `${100 - progress}%`);

          if (progress === 100) {
            $progressbar.remove();
          }
          break;
        default:
          // Check if the fields exist as we have seen a limited number of users getting undefined field
          // errors here. This check is just a precaution and will help limit the number of sentry errors.
          if (this.page.unit && this.page.unit.contentBlocks[tempId]) {
            this.page.unit.contentBlocks[tempId].progress = progress;
            this.scope.$apply();
          }
          break;
      }
    },

    doneFileHandler: function doneFileHandler(event, data) {
      const that = this;
      const entryId = data.entry.id;
      const { tempId } = data.entry;
      BulbAssetLibraryService.api.getEntry(entryId).then((res) => {
        const { entry } = res;
        switch (entry.type) {
          case 'ATTACHED_FILE':
            that.createAttachedFileBlock(entry, tempId);
            break;
          case 'AUDIO':
            that.createAudioBlock(entry, tempId, data.isRecordedAudio);
            break;
          case 'IMAGE':
            that.createImageBlock(entry, tempId);
            break;
          case 'VIDEO':
            that.createVideoBlock(entry, tempId);
            break;
        }
      });
    },

    /**
     * This function is used to handle error when file is unable to process.
     * @param data
     */
    errorFileHandler: function errorFileHandler(data) {
      const $contentBlockElement = jQuery(`#${data.entry.tempId}`);
      $contentBlockElement.empty();
      delete this.page.unit.contentBlocks[data.entry.tempId];
    },

    handleMediaButtonClick: function handleMediaButtonClick(event) {
      event.stopPropagation();

      const that = this;
      let $place = $(that.editor).find('.bulb-media-active');
      if ($place.length === 0) {
        this.handleEnterPress();
        $place = $(this.editor).find('.bulb-media-active');
      }
      this.$lastPlace = $place;

      BulbAssetLibraryService.dialog.image.showSelectionModal(
        (entry) => {
          const deferred = $q.defer();
          const tempId = that.generateTemporaryId();
          that.initContentBlock($place, tempId);
          that.createImageBlock(entry, tempId);
          deferred.resolve();
          return deferred.promise;
        },
        () => {
          BulbAssetLibraryUploadService.uploadImageBySelectionModal(
            that,
            'addFileHandler',
            'submitFileHandler',
            'progressFileHandler',
            'doneFileHandler',
            false, // from within assetlibrary
            'page', // upload state
          );
        },
        () => {
          BulbAssetLibraryService.dialog.closeCurrentModal();
          BulbAssetLibraryGoogleDriveService.openImageTypePicker(
            (oauthToken, id, mimeType, photoParentId) => {
              BulbBusyCoverService.showBusyDuring(
                BulbAssetLibraryService.api.importFromGoogleDrive(
                  oauthToken,
                  null,
                  id,
                  mimeType,
                  photoParentId,
                ).then(
                  (data) => {
                    const tempId = that.generateTemporaryId();
                    data.entry.tempId = tempId;
                    that.initContentBlock($place, tempId);
                    that.doneFileHandler(null, data);
                  },
                ),
              );
            },
          );
        },
        () => {
          RecordEventService
            .recordEvent('google_photos_image', 'google_photos_image_page_content');
          BulbAssetLibraryService.dialog.closeCurrentModal();
          BulbAssetLibraryGooglePhotoService.initializeGooglePhotoPicker(
            (oauthToken, mediaId) => {
              BulbBusyCoverService.showBusyDuring(
                BulbAssetLibraryService.api.importFromGooglePhoto(
                  oauthToken,
                  null,
                  mediaId,
                ).then(
                  (data) => {
                    const tempId = that.generateTemporaryId();
                    data.entry.tempId = tempId;
                    that.initContentBlock($place, tempId);
                    that.doneFileHandler(null, data);
                  },
                ),
              );
            },
          );
        },
        () => {
          BulbAssetLibraryUnsplashService.initializeUnsplashPhotoPicker(
            (photoId) => {
              BulbBusyCoverService.showBusyDuring(
                BulbAssetLibraryUnsplashService.importFromUnsplash(photoId, null).then(
                  (data) => {
                    const tempId = that.generateTemporaryId();
                    data.entry.tempId = tempId;
                    that.initContentBlock($place, tempId);
                    that.doneFileHandler(null, data);
                    BulbAssetLibraryService.dialog.closeCurrentModal();
                  },
                ),
              );
            },
          );
        },
        () => {
          BulbAssetLibraryService.dialog.closeCurrentModal();
          BulbAssetLibraryOneDriveService.initializeOneDrivePicker((oneDriveFileObject) => {
            BulbBusyCoverService.showBusyDuring(
              BulbAssetLibraryOneDriveService.importFromOneDrive(oneDriveFileObject.id, that.currentFolderId)
                .then((data) => {
                  const tempId = that.generateTemporaryId();
                  data.entry.tempId = tempId;
                  that.initContentBlock($place, tempId);
                  that.doneFileHandler(null, data);
                }),
            );
          }, [CONFIG.image.extensions]);
        },

        BulbAssetLibraryUploadService.createImageUploadOptions(
          null,
          that,
          'addFileHandler',
          'submitFileHandler',
          'progressFileHandler',
          'doneFileHandler',
        ),
        false, // image carousel drag and drop
        false, // avatar or cover image upload state
        event.currentTarget,
      );
    },

    handleVideoButtonClick: function handleVideoButtonClick(event) {
      event.stopPropagation();

      const that = this;
      let $place = $(that.editor).find('.bulb-media-active');
      if ($place.length === 0) {
        this.handleEnterPress();
        $place = $(this.editor).find('.bulb-media-active');
      }
      this.$lastPlace = $place;

      BulbAssetLibraryService.dialog.video.showSelectionModal(
        (entry) => {
          const deferred = $q.defer();
          const tempId = that.generateTemporaryId();
          that.initContentBlock($place, tempId);
          that.createVideoBlock(entry, tempId);
          deferred.resolve();
          return deferred.promise;
        },
        () => {
          BulbAssetLibraryUploadService.uploadVideoBySelectionModal(
            that,
            'addFileHandler',
            'submitFileHandler',
            'progressFileHandler',
            'doneFileHandler',
            false, // from within assetlibrary
            'page', // upload state
          );
        },
        () => {
          BulbAssetLibraryService.dialog.closeCurrentModal();
          BulbAssetLibraryGoogleDriveService.openVideoTypePicker(
            (oauthToken, id, mimeType) => {
              BulbBusyCoverService.showBusyDuring(
                BulbAssetLibraryService.api.importFromGoogleDrive(
                  oauthToken,
                  null,
                  id,
                  mimeType,
                ).then(
                  (data) => {
                    const tempId = that.generateTemporaryId();
                    data.entry.tempId = tempId;
                    that.initContentBlock($place, tempId);
                    that.doneFileHandler(null, data);
                  },
                ),
              );
            },
          );
        },
        () => {
          RecordEventService
            .recordEvent('google_photos_video', 'google_photos_video_page_content');
          BulbAssetLibraryService.dialog.closeCurrentModal();
          BulbAssetLibraryGooglePhotoService.initializeGooglePhotoPicker((oauthToken, mediaId) => {
            BulbBusyCoverService.showBusyDuring(
              BulbAssetLibraryService.api.importFromGooglePhoto(
                oauthToken,
                null,
                mediaId,
              ).then(
                (data) => {
                  const tempId = that.generateTemporaryId();
                  data.entry.tempId = tempId;
                  that.initContentBlock($place, tempId);
                  that.doneFileHandler(null, data);
                },
              ),
            );
          }, false, true);
        },
        (videoUrl) => {
          // Handle YouTube videos embedding.
          if (that.isYouTubeUrl(videoUrl)) {
            const tempId = that.generateTemporaryId();
            $place.attr('id', tempId);
            $place.addClass('bulb-media-temp-placeholder');
            that.createEmbedBlock.call(that, null, videoUrl, tempId, null, null, null, null);
            return $q((resolve) => {
              resolve();
            });
          }
          // Handle Vimeo videos embedding.
          return BulbAssetLibraryService.api.createExternalServiceVideo(null, videoUrl)
            .then((res) => {
              BulbAssetLibraryService.dialog.dismissAllModals();
              const tempId = that.generateTemporaryId();
              that.initContentBlock($place, tempId);
              that.createVideoBlock(res.entry, tempId);
            }, (error) => {
              BulbErrorService.showError(error.data.message, error).then(() => {
                BulbAssetLibraryService.dialog.closeCurrentModal();
              });
            });
        },
        () => {
          BulbAssetLibraryService.dialog.closeCurrentModal();
          BulbAssetLibraryOneDriveService.initializeOneDrivePicker((oneDriveFileObject) => {
            if (oneDriveFileObject.file && oneDriveFileObject.file.mimeType.indexOf('video') > -1) {
              BulbBusyCoverService.showBusyDuring(
                BulbAssetLibraryOneDriveService.importFromOneDrive(oneDriveFileObject.id, that.currentFolderId)
                  .then((data) => {
                    const tempId = that.generateTemporaryId();
                    data.entry.tempId = tempId;
                    that.initContentBlock($place, tempId);
                    that.doneFileHandler(null, data);
                  }),
              );
            } else {
              BulbAssetLibraryService.dialog.showUnsupportedFileFormatError('error_unsupported_file_format');
            }
          }, undefined);
        },
        BulbAssetLibraryUploadService.createVideoUploadOptions(
          null,
          that,
          'addFileHandler',
          'submitFileHandler',
          'progressFileHandler',
          'doneFileHandler',
        ),
        event.currentTarget,
      );
    },

    handleAudioButtonClick: function handleAudioButtonClick(event) {
      event.stopPropagation();

      const that = this;
      let $place = $(that.editor).find('.bulb-media-active');
      if ($place.length === 0) {
        this.handleEnterPress();
        $place = $(this.editor).find('.bulb-media-active');
      }
      this.$lastPlace = $place;

      BulbAssetLibraryService.dialog.audio.showSelectionModal(
        (entry) => {
          const deferred = $q.defer();
          const tempId = that.generateTemporaryId();
          that.initContentBlock($place, tempId);
          that.createAudioBlock(entry, tempId, false);
          deferred.resolve();
          return deferred.promise;
        },
        () => {
          BulbAssetLibraryUploadService.uploadAudioBySelectionModal(
            that,
            'addFileHandler',
            'submitFileHandler',
            'progressFileHandler',
            'doneFileHandler',
            false, // from within assetLibrary
            'page', // upload state
          );
        },
        () => {
          BulbAssetLibraryService.dialog.closeCurrentModal();
          BulbAssetLibraryGoogleDriveService.openAudioTypePicker(
            (oauthToken, id, mimeType, photoParentId) => {
              BulbBusyCoverService.showBusyDuring(
                BulbAssetLibraryService.api.importFromGoogleDrive(
                  oauthToken,
                  null,
                  id,
                  mimeType,
                  photoParentId,
                ).then(
                  (data) => {
                    const tempId = that.generateTemporaryId();
                    data.entry.tempId = tempId;
                    that.initContentBlock($place, tempId);
                    that.doneFileHandler(null, data);
                  },
                ),
              );
            },
          );
        },
        () => {
          BulbAssetLibraryService.dialog.closeCurrentModal();
          BulbAssetLibraryOneDriveService.initializeOneDrivePicker((oneDriveFileObject) => {
            BulbBusyCoverService.showBusyDuring(
              BulbAssetLibraryOneDriveService.importFromOneDrive(oneDriveFileObject.id, that.currentFolderId)
                .then((data) => {
                  const tempId = that.generateTemporaryId();
                  data.entry.tempId = tempId;
                  that.initContentBlock($place, tempId);
                  that.doneFileHandler(null, data);
                }),
            );
          }, [CONFIG.audio.extensions]);
        },
        BulbAssetLibraryUploadService.createAudioUploadOptions(
          null,
          that,
          'addFileHandler',
          'submitFileHandler',
          'progressFileHandler',
          'doneFileHandler',
        ),
        false, // from within the assetLibrary
        event.currentTarget,
      );
    },

    handleAttachmentButtonClick: function handleAttachmentButtonClick(event) {
      event.stopPropagation();

      const that = this;
      let $place = $(that.editor).find('.bulb-media-active');
      if ($place.length === 0) {
        this.handleEnterPress();
        $place = $(this.editor).find('.bulb-media-active');
      }
      this.$lastPlace = $place;

      BulbAssetLibraryService.dialog.attachedFile.showSelectionModal(
        (entry) => {
          const deferred = $q.defer();
          const tempId = that.generateTemporaryId();
          that.initContentBlock($place, tempId);
          that.createAttachedFileBlock(entry, tempId);
          deferred.resolve();
          return deferred.promise;
        },
        () => {
          BulbAssetLibraryUploadService.uploadAttachedFileBySelectionModal(
            that,
            'addFileHandler',
            'submitFileHandler',
            'progressFileHandler',
            'doneFileHandler',
            false, // from within assetLibrary
            'page', // upload state
          );
        },
        () => {
          BulbAssetLibraryService.dialog.closeCurrentModal();
          BulbAssetLibraryGoogleDriveService.openAttachedFileTypePicker(
            (oauthToken, id, mimeType, parentId, url, code) => {
              // We check to ensure that the mimeType is a document that can be embedded into the page,
              // if it can be we do the embed instead of telling the user to do it themselves.
              // List of google documents that can be embedded can be found here
              // https://developers.google.com/drive/v3/web/mime-types
              // BULBAPP-7264: Removed  mimeType === 'application/pdf' condition to fix
              // PDF upload 403 error --> only supports Docs Editors files
              if (mimeType === 'application/vnd.google-apps.document'
                || mimeType === 'application/vnd.google-apps.spreadsheet'
                || mimeType === 'application/vnd.google-apps.drawing'
                || mimeType === 'application/vnd.google-apps.presentation'
                || mimeType === 'application/vnd.google-apps.form') {
                const tempId = that.generateTemporaryId();
                $place.attr('id', tempId);
                $place.addClass('bulb-media-temp-placeholder');
                BulbAssetLibraryGoogleDriveService.enableShareableLink(id).then((result) => {
                  if (result) {
                    url = result.url;
                  }
                  that.createEmbedBlock.call(that, null, url, tempId, null, null, id, code);
                }, (error) => {
                  if (error) {
                    if (error.code === 403) {
                      BulbAssetLibrarySnapshotPdfService.importPdfForOrgRestrictedDoc(
                        id,
                        that.page.unit.id,
                        code,
                      ).then((response) => {
                        that.createAttachedFileBlock.call(that, response, tempId);
                      });
                    } else {
                      BulbErrorService.showError(`${$translate.instant('embed_error_message_basic')
                      }<a href=\"${url}\" target=\"_blank\">${url}</a>`, error);
                    }
                  }
                });
              } else if (mimeType.indexOf('application/vnd.google-apps') === 0
                && mimeType !== 'application/vnd.google-apps.photo') {
                // Unsupported embed, tell the user we do not support attaching or embedding this file
                $translate('error_google_drive_not_support_google_document').then((msg) => {
                  BulbAssetLibraryService.dialog.showMessage(null, msg);
                });
              } else {
                BulbBusyCoverService.showBusyDuring(
                  BulbAssetLibraryService.api.importFromGoogleDrive(
                    oauthToken,
                    null,
                    id,
                    mimeType,
                    null,
                    true,
                  ).then(
                    (data) => {
                      const tempId = that.generateTemporaryId();
                      data.entry.tempId = tempId;
                      that.initContentBlock($place, tempId);
                      that.doneFileHandler(null, data);
                    },
                  ),
                );
              }
            },
          );
        },
        () => {
          BulbAssetLibraryService.dialog.closeCurrentModal();
          BulbAssetLibraryOneDriveService.initializeOneDrivePicker((oneDriveFileObject) => {
            const mimeType = (oneDriveFileObject.file) ? oneDriveFileObject.file.mimeType : undefined;
            const tempId = that.generateTemporaryId();
            if (!mimeType || mimeType === 'text/plain') {
              // Unsupported embed, tell the user we do not support attaching or embedding this file
              BulbAssetLibraryService.dialog.showUnsupportedFileFormatError(
                'error_unsupported_file_format',
              );
            } else if (mimeType.startsWith('application/vnd.openxmlformats')) {
              if (oneDriveFileObject.parentReference.driveType === 'business') {
                BulbBusyCoverService.showBusyDuring(
                  BulbAssetLibraryOneDriveService.importFromOneDrive(oneDriveFileObject.id, that.currentFolderId)
                    .then((data) => {
                      data.entry.tempId = tempId;
                      that.initContentBlock($place, tempId);
                      that.doneFileHandler(null, data);
                    }),
                );
              } else {
                const url = BulbAssetLibraryOneDriveService
                  .getItemContentByShareId(oneDriveFileObject.permissions[0].shareId);
                $place.attr('id', tempId);
                $place.addClass('bulb-media-temp-placeholder');
                that.createEmbedBlock.call(that, null, url, tempId, null, null, null, null);
              }
            } else {
              BulbBusyCoverService.showBusyDuring(
                BulbAssetLibraryOneDriveService.importFromOneDrive(oneDriveFileObject.id, that.currentFolderId)
                  .then((data) => {
                    data.entry.tempId = tempId;
                    that.initContentBlock($place, tempId);
                    that.doneFileHandler(null, data);
                  }),
              );
            }
          }, undefined, undefined, true);
        },
        BulbAssetLibraryUploadService.createAttachedFileUploadOptions(
          null,
          that,
          'addFileHandler',
          'submitFileHandler',
          'progressFileHandler',
          'doneFileHandler',
        ),
        event.currentTarget,
      );
    },

    handleEmbedButtonClick: function handleEmbedButtonClick(event) {
      event.stopPropagation();

      const that = this;
      let $place = $(this.editor).find('.bulb-media-active');
      if ($place.length === 0) {
        this.handleEnterPress();
        $place = $(this.editor).find('.bulb-media-active');
      }
      let modalInstance;

      const modalOptions = {
        templateUrl: 'ajs/components/contenteditor/embedblockform.html',
        controller: ['$scope', function embedBlockFormModalController($scope) {
          /**
           * This function handles the paste event for MS Edge.
           *
           * @param pasteEvent The original event.
           * @param form The embedForm that we use to create the embed block.
           */
          $scope.handlePaste = function handlePaste(pasteEvent, form) {
            // Only add this to users that are using MS Edge or FireFox, as to how they handle past events.
            if (BulbBrowserService.isMsEdge() || BulbBrowserService.isFireFox()) {
              pasteEvent.preventDefault();

              // We want to be explicit of where we paste the content
              const $embedBlockFormURLInput = jQuery('#url');
              // Grab the data from the clipboard
              const pasteData = (pasteEvent.originalEvent || pasteEvent).clipboardData.getData('text/plain');

              // Append the data to the input field
              $embedBlockFormURLInput.val(pasteData);

              // Need to update the state of the form manually.
              form.$setDirty();
              form.$invalid = false;
              form.$valid = true;

              // Pass the value of the url that the user pasted to the embedBlockCtrl.
              $scope.embedBlockFormCtrl.url = pasteData;
            }
          };

          this.submitUrl = function submitUrl(form) {
            const { url } = $scope.embedBlockFormCtrl;

            // If embed URL is a Vimeo URL, handle with asset library service
            if (url.indexOf('vimeo.com') > -1) {
              // Handle Vimeo videos embedding.
              BulbAssetLibraryService.api.createExternalServiceVideo(null, url)
                .then((res) => {
                  const tempId = that.generateTemporaryId();
                  that.initContentBlock($place, tempId);
                  that.createVideoBlock(res.entry, tempId);
                });
            } else if (that.isYouTubeUrl(url)) {
              // Handle YouTube video embedding.
              const temporaryId = that.generateTemporaryId();
              $place.attr('id', temporaryId);
              $place.addClass('bulb-media-temp-placeholder');
              that.createEmbedBlock.call(that, null, url, temporaryId, null, null, null, null);
            } else {
              // For the embed url we first need to make $place a temporary placeholder for it:
              const tempId = that.generateTemporaryId();
              $place.attr('id', tempId);
              $place.addClass('bulb-media-temp-placeholder');
              let fileId;
              if (url.indexOf('docs.google.com') !== -1) {
                // Get fileId from url.
                const parts = url.split('d/');
                fileId = parts[1].split('/')[0];
                // The Google form url when copied from 'send via link' contains e/ after d/
                // So to get actual file id from url we need to remove it.
                if (fileId.indexOf('e/') !== -1) {
                  fileId = fileId.split('/')[1];
                }
              }
              if (url.indexOf('1drv.ms') > -1) {
                BulbAssetLibraryOneDriveService.getSharedEmbedUrl(url).then((response) => {
                  $scope.embedBlockFormCtrl.url = response;
                  that.createEmbedBlock.call(
                    that,
                    form,
                    $scope.embedBlockFormCtrl.url,
                    tempId,
                    modalInstance,
                    null,
                    fileId,
                    null,
                  );
                }, (error) => {
                  BulbAssetLibraryOneDriveService.showEmbedError({
                    fontAwesomeCode: 'fas fa-clouds',
                    titleCode: 'embed_onedrive_url_error_title',
                    bodyCode: error,
                    showCancelButton: false,
                    windowClass: 'updated-modal-design bulb-aero-modal',
                  });
                });
              } else if (url.indexOf('onedrive.live.com') > -1) {
                BulbAssetLibraryOneDriveService.createSharableLinkForDocument(url)
                  .then((response) => {
                    $scope.embedBlockFormCtrl.url = response;
                    that.createEmbedBlock.call(
                      that,
                      form,
                      $scope.embedBlockFormCtrl.url,
                      tempId,
                      modalInstance,
                      null,
                      fileId,
                      null,
                    );
                  }, (error) => {
                    BulbAssetLibraryOneDriveService.showEmbedError({
                      fontAwesomeCode: 'fas fa-clouds',
                      titleCode: 'embed_onedrive_url_error_title',
                      bodyCode: (error.data) ? error.data.message : error,
                      showCancelButton: false,
                      windowClass: 'updated-modal-design bulb-aero-modal',
                    });
                  });
              } else if (url.indexOf('sharepoint.com') > -1) {
                /**
                 * OneDrive for business does not support embeds, so showing error if the shared url
                 * belongs to OneDrive business account.
                 */
                BulbAssetLibraryOneDriveService.showEmbedError({
                  fontAwesomeCode: 'fas fa-exclamation-triangle',
                  titleCode: 'embed_onedrive_account_not_supported_title',
                  cancelButtonCode: 'onboarding_got_it_button',
                  bodyCode: 'embed_onedrive_account_not_supported',
                  showCancelButton: true,
                  showOkButton: false,
                  windowClass: 'updated-modal-design bulb-aero-modal bulb-onedrive-embed-modal',
                });
              } else {
                that.createEmbedBlock.call(
                  that,
                  form,
                  $scope.embedBlockFormCtrl.url,
                  tempId,
                  modalInstance,
                  null,
                  fileId,
                  null,
                );
              }
            }
            BulbAssetLibraryService.dialog.closeCurrentModal();
          };
          this.closeModal = function closeModal(closeEvent) {
            BulbModalService.clearDialog(closeEvent);
          };
        }],
        controllerAs: 'embedBlockFormCtrl',
        scope: this.scope,
        backdrop: true,
        windowClass: 'bulb-media-transient',
        focusAfterClosed: event.currentTarget,
      };

      // Add this to the parent element so that you can't resize the dialog on IE.
      modalInstance = BulbModalService.showCustomModal(modalOptions);
    },

    showImagePreview: function showImagePreview(img, data) {
      const { tempId } = data.entry;
      const $place = $(this.editor).find(`#${tempId}`);

      // Create temporary uploading image block:
      // Add the block to the unit blocks:
      this.page.unit.contentBlocks[tempId] = {
        id: tempId,
        img,
        blockType: 'UPLOADING_IMAGE',
        data,
      };

      // TODO CLEAN - use a template rather than embedded string.
      // Swap in a content-block element that points to the new temporary block:
      $place.replaceWith($compile(`<content-block id="${tempId
      }" contenteditable="false" class="bulb-media-transient align-center"></content-block>`)(this.scope));
      // this.scope.$apply();
      // add newline after content block
      this.appendNewLine($(this.editor).find(`#${tempId}`));
      // this.selectBlock($(this.editor).find('#' + data.tempId)[0]);

      // TODO DESIGN : what about changing the cursor to a busy cursor during the upload and processing?
      // or would that be confusing since you can still interact in order to cancel the upload?
    },

    showUploadingFileProgressbar: function showUploadingFileProgressbar(data) {
      const { tempId } = data.entry;
      const $place = $(this.editor).find(`#${tempId}`);

      // Create temporary uploading file block:
      // Add the block to the unit blocks:
      this.page.unit.contentBlocks[tempId] = {
        id: tempId,
        blockType: 'UPLOADING_FILE',
        data,
        progress: 0,
      };

      // TODO CLEAN - use a template rather than embedded string.
      // Swap in a content-block element that points to the new temporary block:
      $place.replaceWith($compile(`<content-block id="${tempId
      }" contenteditable="false" class="bulb-media-transient align-center"></content-block>`)(this.scope));

      // this.scope.$apply();

      this.selectBlock($(this.editor).find(`#${tempId}`)[0]);
      // add newline after content block
      this.appendNewLine($(this.editor).find(`#${tempId}`));
      // TODO DESIGN : what about changing the cursor to a busy cursor during the upload and processing?
      // or would that be confusing since you can still interact in order to cancel the upload?
    },

    updateId: function updateId(data) {
      const $place = $(this.editor).find(`#${data.tempId}`);
      $place.attr('id', data.result.id);
      this.trigger('editableInput', null, this.editor);
    },

    cancelUpload: function cancelUpload(event, contentBlock) {
      const that = this;
      // TODO ISSUE : confirm cancel
      contentBlock.data.jqXHR.abort();
      delete this.page.unit.contentBlocks[contentBlock.id];
      const $place = $(this.editor).find(`#${contentBlock.id}`);
      createNewParagraphData().then((data) => {
        $place.replaceWith(data);
        // TODO ISSUE : update the selection to the empty paragraph were we were uploading to

        // NOTE: We need to explicitly hide the toolbar when a user aborts the upload process of a content-block
        // Due to how the implementation of medium-editor's 'updateToolbar' function works. It currently doesn't
        // Handle updating the toolbar if the toolbar has an 'empty' state. We could choose to override this
        // Behaviour in the future to allow the 'updateToolbar' function to handle an empty toolbar state.
        // Another location for which we need to do this is when a user deletes a content-block.
        const toolbar = that.base.getExtensionByName('toolbar');
        $timeout(() => {
          toolbar.hideToolbar();
        }, CONTENT_EDITOR.GRAB_TOOLBAR_TIMEOUT);
      });
    },

    generateTemporaryId: function generateTemporaryId() {
      // Take the current time converted to base 36 to shorten it and append a random string to it.
      return `${Date.now().toString(36)}_${Foundation.utils.random_str(6)}`; // jshint ignore:line
    },

    createImageBlock: function createImageBlock(entry, contentBlockElementId) {
      const that = this;

      const uploadImageDTO = {
        assetLibraryEntryId: entry.id,
      };

      SerialSaveService.save({
        showBusy: true,
        save: () => Restangular.one('units', this.page.unit.id)
          .customPOST(uploadImageDTO, 'imageblock')
          .then((response) => {
            that.page.unit.contentBlocks[response.contentBlock.id] = response.contentBlock;

            // update content with new content block information
            // find placeholder, and replace with content block:
            const $contentBlockElement = jQuery(`#${contentBlockElementId}`);
            // TODO POLISH : is there a way to replace the preview image with this block without a flash and
            // layout change?

            const isSelected = $contentBlockElement.find('.selected-block').length > 0;
            $contentBlockElement.replaceWith($compile(`<content-block id="${response.contentBlock.id
            }" contenteditable="false" class="align-center"></content-block>`)(that.scope));
            // TODO CLEAN - use a template rather than embedded string.

            // TODO POLISH : is it possible to prevent the flash and re-layout when we replace the local image
            // with the content block?

            if (isSelected) {
              // Have to wait until the current digest finishes processing, or else the content-block
              // will still be empty, and the selectBlock will fail.
              $timeout(() => {
                that.selectBlock(jQuery(`#${response.contentBlock.id}`)[0]);
              });
            }

            // Update share status to reflect that the page has been edited:
            const ShareTransition = UnitStateStore.getShareTransitionEnum();
            UnitStateStore.transitionShareStatus(ShareTransition.EDIT);

            that.trigger('editableInput', null, that.editor);
            that.focusOnNewBlankLine(response.contentBlock.id);
            that.updateButtonOnContentBlockRendered(response.contentBlock.id);
          }),
      });
    },

    createVideoBlock: function createVideoBlock(entry, contentBlockElementId) {
      const that = this;

      const uploadVideoDTO = {
        assetLibraryEntryId: entry.id,
      };

      SerialSaveService.save({
        showBusy: true,
        save: () => Restangular.one('units', that.page.unit.id)
          .customPOST(uploadVideoDTO, 'videoblock')
          .then((response) => {
            that.page.unit.contentBlocks[response.contentBlock.id] = response.contentBlock;

            // update content with new content block information
            // find placeholder, and replace with content block:
            const $contentBlockElement = jQuery(`#${contentBlockElementId}`);
            const isSelected = $contentBlockElement.find('.selected-block').length > 0;
            $contentBlockElement.replaceWith($compile(`<content-block id="${response.contentBlock.id
            }" contenteditable="false" class="align-center"></content-block>`)(that.scope));
            // TODO CLEAN - use a template rather than embedded string.

            if (isSelected) {
              // Have to wait until the current digest finishes processing, or else the content-block
              // will still be empty, and the selectBlock will fail.
              $timeout(() => {
                that.selectBlock(jQuery(`#${response.contentBlock.id}`)[0]);
              });
            }

            // Update share status to reflect that the page has been edited:
            const ShareTransition = UnitStateStore.getShareTransitionEnum();
            UnitStateStore.transitionShareStatus(ShareTransition.EDIT);

            that.trigger('editableInput', null, that.editor);
            that.focusOnNewBlankLine(response.contentBlock.id);
            that.updateButtonOnContentBlockRendered(response.contentBlock.id);
            that.updateButtonAfterTranscodingDone(response.contentBlock.id, true);
          }),
      });
    },

    /**
     *
     * @param entry
     * @param contentBlockElementId
     * @param isRecordedAudio
     */
    createAudioBlock: function createAudioBlock(entry, contentBlockElementId, isRecordedAudio) {
      const that = this;

      const uploadAudioDTO = {
        assetLibraryEntryId: entry.id,
      };

      SerialSaveService.save({
        showBusy: true,
        save: () => Restangular.one('units', that.page.unit.id)
          .customPOST(uploadAudioDTO, 'audioblock')
          .then((response) => {
            that.page.unit.contentBlocks[response.contentBlock.id] = response.contentBlock;

            if (isRecordedAudio) {
              const audioRecordingEvent = new CustomEvent('audioRecordingDone', {
                detail: {
                  contentBlockId: response.contentBlock.id,
                },
              });
              $window.dispatchEvent(audioRecordingEvent);
            }

            // update content with new content block information
            // find placeholder, and replace with content block:
            const $contentBlockElement = jQuery(`#${contentBlockElementId}`);
            const isSelected = $contentBlockElement.find('.selected-block').length > 0;
            $contentBlockElement.replaceWith($compile(`<content-block id="${response.contentBlock.id
            }" contenteditable="false" class="align-center"></content-block>`)(that.scope));
            // TODO CLEAN - use a template rather than embedded string.

            if (isSelected) {
              // Have to wait until the current digest finishes processing, or else the content-block
              // will still be empty, and the selectBlock will fail.
              $timeout(() => {
                that.selectBlock(jQuery(`#${response.contentBlock.id}`)[0]);
              });
            }

            // Update share status to reflect that the page has been edited:
            const ShareTransition = UnitStateStore.getShareTransitionEnum();
            UnitStateStore.transitionShareStatus(ShareTransition.EDIT);

            that.trigger('editableInput', null, that.editor);
            that.focusOnNewBlankLine(response.contentBlock.id);
            that.updateButtonOnContentBlockRendered(response.contentBlock.id);
            that.updateButtonAfterTranscodingDone(response.contentBlock.id);
          }),
      });
    },

    createAttachedFileBlock: function createAttachedFileBlock(entry, contentBlockElementId) {
      const that = this;

      const uploadAttachedFileDTO = {
        assetLibraryEntryId: entry.id,
      };

      SerialSaveService.save({
        showBusy: true,
        save: () => Restangular.one('units', that.page.unit.id)
          .customPOST(uploadAttachedFileDTO, 'attachedfileblock')
          .then((response) => {
            // The response will have the embedFailure property when the uploaded file has an extension we can
            // embed, but failed to embed. The purpose of the embedFailure property is to alert the user that
            // we normally embed the file type, but were unable to and just created an AttachedFileBlock
            // instead. See createAttachedFileBlock method in BulbPageController.
            if (response.hasOwnProperty('embedFailure')) {
              $translate(response.embedFailure.toLowerCase()).then((fileType) => {
                $translate('attached_file_embed_failure_body', { fileType }).then((body) => {
                  BulbModalService.show({
                    titleCode: 'attached_file_embed_failure_title',
                    bodyCode: body,
                    showCancelButton: false,
                  });
                });
              });
            } else if (response.hasOwnProperty('embedSizeExceeded')) {
              // TODO CLEAN handle more gracefully after release
              let maxSize;
              const fileExtension = response.embedSizeExceeded.toLowerCase();
              if (fileExtension === 'xls' || fileExtension === 'xlsx') {
                maxSize = 5;
              } else {
                maxSize = 10;
              }
              $translate(fileExtension).then((fileType) => {
                $translate('attached_file_embed_too_large_body', { fileType, maxSize })
                  .then((body) => {
                    BulbModalService.show({
                      titleCode: 'attached_file_embed_failure_title',
                      bodyCode: body,
                      showCancelButton: false,
                    });
                  });
              });
            }

            that.page.unit.contentBlocks[response.contentBlock.id] = response.contentBlock;

            // update content with new content block information
            // find placeholder, and replace with content block:
            const $contentBlockElement = jQuery(`#${contentBlockElementId}`);
            const isSelected = $contentBlockElement.find('.selected-block').length > 0;
            $contentBlockElement.replaceWith($compile(`<content-block id="${response.contentBlock.id
            }" contenteditable="false"></content-block>`)(that.scope));
            // TODO CLEAN - use a template rather than embedded string.

            if (isSelected) {
              // Have to wait until the current digest finishes processing, or else the content-block
              // will still be empty, and the selectBlock will fail.
              $timeout(() => {
                that.selectBlock(jQuery(`#${response.contentBlock.id}`)[0]);
              });
            }

            // Update share status to reflect that the page has been edited:
            const ShareTransition = UnitStateStore.getShareTransitionEnum();
            UnitStateStore.transitionShareStatus(ShareTransition.EDIT);

            that.trigger('editableInput', null, that.editor);
            that.focusOnNewBlankLine(response.contentBlock.id);
            that.updateButtonOnContentBlockRendered(response.contentBlock.id, true);
          }),
      });
    },

    deleteSelectedBlock: function deleteSelectedBlock() {
      const that = this;
      let $selectedBlock = $(this.selection).closest('content-block');
      const contentBlock = this.page.unit.contentBlocks[$selectedBlock.attr('id')];

      // TODO ISSUE : disable keyboard interaction during dialog, or else the dialog isn't really modal.

      $translate(`content_block_type_${contentBlock.blockType}`).then((type) => {
        $translate('modal_delete_confirm_body', { type }).then((bodyText) => {
          BulbModalService.show({
            titleCode: 'modal_delete_confirm_title',
            bodyText,
            okButtonCode: 'button_delete',
            parent: '.bulb-page-content',
            windowClass: 'bulb-media-transient',
          }).then(() => {
            SerialSaveService.save({
              showBusy: true,
              save: () => Restangular.one('units', that.page.unit.id)
                .one('blocks', contentBlock.id)
                .remove()
                .then(() => {
                  delete that.page.unit.contentBlocks[contentBlock.id];

                  // Update share status to reflect that the page has been edited:
                  const ShareTransition = UnitStateStore.getShareTransitionEnum();
                  UnitStateStore.transitionShareStatus(ShareTransition.EDIT);

                  // Clicking on the modal dialog causes the selection to change (as can keyboard
                  // interaction), so get the block based on the id, so we can remove it.  Then set the
                  // selection caret, so it remains at the same location as the removed block within
                  // the editor.
                  $selectedBlock = $(`#${contentBlock.id}`);
                  $selectedBlock = $('<p><br/></p>').replaceAll($selectedBlock);
                  that.placeSelectionCaretAtStart($selectedBlock[0]);
                  that.unselectBlock();
                  that.trigger('editableInput', null, that.editor);
                  // Explicitly hide the toolbar when a user deletes a content-block
                  const toolbar = that.base.getExtensionByName('toolbar');
                  $timeout(() => {
                    toolbar.hideToolbar();
                  }, CONTENT_EDITOR.GRAB_TOOLBAR_TIMEOUT);
                }),
            });
          });
        });
      });
    },

    createEmbedBlock: function
    createEmbedBlock(form, url, tempId, modalInstance, attachedFileAssetId, googleFileId, googleCode) {
      const that = this;

      const embedBlockDTO = {
        url,
        attachedFileAssetId: attachedFileAssetId || null,
      };

      const contentBlockElement = jQuery(`#${tempId}`);

      SerialSaveService.save({
        showBusy: true,
        preventRetry: true,
        save: () => Restangular.one('units', that.page.unit.id)
          .customPOST(embedBlockDTO, 'embedblock')
          .then((response) => {
            // Check if embed doc is a Google asset so request api to generate snapshot pdf
            // If the embed doc is a Google form then skip creating pdf snapshot.
            if (googleFileId && url.indexOf('forms/') === -1) {
              BulbAssetLibrarySnapshotPdfService.importPdfFromGoogleDrive(
                googleFileId,
                googleCode,
                response.contentBlock.id,
                that.page.unit.id,
              );
            }
            that.page.unit.contentBlocks[response.contentBlock.id] = response.contentBlock;

            // Find the temporary placeholder, and replace with content block
            contentBlockElement.replaceWith($compile(`<content-block id="${response.contentBlock.id
            }" contenteditable="false"></content-block>`)(that.scope));

            // Update share status to reflect that the page has been edited:
            const ShareTransition = UnitStateStore.getShareTransitionEnum();
            UnitStateStore.transitionShareStatus(ShareTransition.EDIT);

            // TODO CLEAN - use a template rather than embedded string.

            // TODO CLEAN : Is this necessary, or do we call it as necessary from within media-extension?
            that.trigger('editableInput', null, that.editor);
            that.focusOnNewBlankLine(response.contentBlock.id);
            that.updateButtonOnContentBlockRendered(response.contentBlock.id);
            if (modalInstance) {
              modalInstance.close();
            } else {
              BulbAssetLibraryService.dialog.dismissAllModals();
            }
          }),
      }).catch((error) => {
        if (modalInstance) {
          modalInstance.close();
        }
        if (error.data?.errorMap && form) {
          // allow the form controller to handle field validation errors:
          form.handleRemoteErrors(error);
        } else if (googleFileId) {
          // Check if file is google asset to get permission from user to make url public.
          BulbAssetLibraryGoogleDriveService.getShareableLinkForUrl(googleFileId)
            .then((response) => {
              that.createEmbedBlock.call(
                that,
                form,
                response.url,
                tempId,
                modalInstance,
                null,
                googleFileId,
                response.code,
              );
            }, (getShareableLinkError) => {
              if (getShareableLinkError.code === 403) {
                BulbAssetLibrarySnapshotPdfService.importPdfForOrgRestrictedDoc(
                  googleFileId,
                  that.page.unit.id,
                  googleCode,
                ).then((response) => {
                  that.createAttachedFileBlock.call(that, response, tempId);
                });
              }
            });
        } else {
          // Make sure to remove the placeholder if it fails, otherwise the placeholder
          // stays on the page as a broken, blank line and the toolbar will not display.
          contentBlockElement.remove();

          BulbModalService.show({
            fontAwesomeCode: 'fas fa-comment-exclamation',
            titleCode: 'modal_oops_title',
            bodyCode: 'pageview_embed_not_supported',
            showCancelButton: false,
            windowClass: 'updated-modal-design bulb-aero-modal',
          });
        }
      });

      const target = $(`#${tempId}`);
      if (that.isLastContent(target)) {
        that.appendNewLine(target);
      }
    },

    appendNewLine: function appendNewLine(target) {
      return createNewParagraphData().then((data) => target.after(data));
    },

    isLastContent: function isLastContent(target) {
      return target.next().hasClass('bulb-content-trailer');
    },

    focusPageTitle: function focusPageTitle(offset) {
      const $title = jQuery('.bulb-page-title');

      // blur
      $(window.getSelection().getRangeAt(0).startContainer).blur();

      // focus
      $title.focus();

      if (offset === undefined) {
        offset = $title.val().length;
      }

      const title = $title[0];
      if (title.createTextRange) {
        const range = title.createTextRange();
        range.move('character', offset);
        range.select();
      } else if (title.setSelectionRange) {
        title.setSelectionRange(offset, offset);
      }

      return $title;
    },

    /**
     * Update the button on rendering the content block on the DOM.
     *
     * @param id of the HTML attribute to be rendered.
     */
    updateButtonOnContentBlockRendered: function updateButtonOnContentBlockRendered(id, isFileAttached) {
      const that = this;
      const targetNode = document.getElementById(id);

      const observer = new MutationObserver((mutationsList, observer) => {
        for (const { addedNodes } of mutationsList) {
          for (const node of addedNodes) {
            if (node instanceof Element) {
              let targetNode;

              /**
               * Check for the tags created and content block is fully loaded.
               * iframe tag created for the video, file and embed URL upload
               * video tag created for the audio and video upload
               * img tag created for the image upload
               * bulb-page-attached-file-icon class create for the exceeded file size.
               */
              const blockToObserve = node.querySelector('iframe')
                || node.querySelector('img') || node.querySelector('.bulb-asset-transcoding');

              if (node.nodeName === 'IFRAME') {
                targetNode = node;
              } else if (blockToObserve) {
                targetNode = blockToObserve;
              } else if (node.querySelector('.bulb-page-attached-file-icon') && isFileAttached) {
                that.contentBlockLoaded();
                observer.disconnect();
              }

              if (targetNode) {
                if (targetNode.complete) {
                  that.contentBlockLoaded();
                  observer.disconnect();
                } else {
                  targetNode.addEventListener('load', that.contentBlockLoaded());
                  observer.disconnect();
                }
              }
            }
          }
        }
      });

      observer.observe(targetNode, { subtree: true, childList: true });
    },

    /**
     * To update the position of buttons after transcoding is finish and actual content is displayed
     * @param blockId content block id
     * @param isVideoBlock true, if the transcoding is for video.
     */
    updateButtonAfterTranscodingDone: function updateButtonAfterTranscodingDone(blockId, isVideoBlock) {
      const that = this;
      const targetNode = document.getElementById(blockId);
      const observer = new MutationObserver((mutationsList, observer) => {
        for (const { addedNodes } of mutationsList) {
          for (const node of addedNodes) {
            if (node instanceof Element) {
              if (isVideoBlock) {
                const contentBlock = node.querySelector(`#content-block-${blockId}`);
                if (contentBlock) {
                  that.contentBlockLoaded();
                  observer.disconnect();
                }
              } else {
                const contentBlock = node.querySelector('bulb-jwplayer');
                if (contentBlock && contentBlock.hasChildNodes()) {
                  that.contentBlockLoaded();
                  observer.disconnect();
                }
              }
            }
          }
        }
      });

      observer.observe(targetNode, { subtree: true, childList: true });
    },

    /**
     * Update the media toolbar position on content block fully loaded.
     */
    contentBlockLoaded: function contentBlockLoaded() {
      this.updateButtons();
    },

    /**
     * This function focuses the new line after inserting assets to the page.
     *
     * @param id Id of the element inserted.
     */
    focusOnNewBlankLine: function focusOnNewBlankLine(id) {
      const that = this;
      const $contentBlocks = $('content-block');
      const $lastContentBlock = $filter('filter')($contentBlocks, { id });
      const nextElement = $contentBlocks[$contentBlocks.length - 1] === $lastContentBlock[0]
        ? $lastContentBlock[0].nextElementSibling : $lastContentBlock[0];
      let $nextNode;
      if (nextElement) {
        $nextNode = $(`#${nextElement.getAttribute('id')}`);
      }
      if ($nextNode && $nextNode.selector !== '#null') {
        $timeout(() => {
          that.placeSelectionCaretAtStart($nextNode[0]);
        });
      }
    },

    /**
     * Method ot check, whether the provided url is a valid YouTube url or not.
     * @param url - URL to check.
     * @returns {boolean}
     */
    isYouTubeUrl: function isYouTubeUrl(url) {
      return (url.indexOf('youtube.com') > -1 || url.indexOf('youtu.be') > -1 || url.indexOf('v=') > -1);
    },
  });

  return {
    CreateExtension: function CreateExtension(options) {
      return new BulbInsertBlockExtension(options);
    },
  };
}
